import React, { useState, useContext, useEffect, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';

/**
 * MFA Verification component shown during login when MFA is enabled.
 * 
 * Supports TOTP (authenticator app) verification.
 */
const MFAVerification = ({ sessionToken, username, onVerified, onCancel }) => {
  const auth = useContext(AuthContext);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef([]);
  
  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);
  
  const handleInputChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all digits are entered
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };
  
  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6).split('');
        const newCode = [...code];
        digits.forEach((digit, i) => {
          if (i < 6) newCode[i] = digit;
        });
        setCode(newCode);
        
        // Focus the next empty input or the last one
        const nextEmpty = newCode.findIndex(c => !c);
        inputRefs.current[nextEmpty >= 0 ? nextEmpty : 5]?.focus();
        
        // Auto-submit if all digits are filled
        if (digits.length === 6) {
          handleVerify(digits.join(''));
        }
      });
    }
  };
  
  const handleVerify = async (fullCode = null) => {
    const verificationCode = fullCode || code.join('');
    
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      const result = await auth.verifyMfa(sessionToken, verificationCode, username);
      
      if (result.success) {
        onVerified();
      } else {
        setError(result.error || 'Invalid verification code');
        // Clear the code on error
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      setError(error.message || 'Verification failed');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="login-container">
      <div className="login-form" style={{ maxWidth: '450px' }}>
        <div className="text-center mb-4">
          <i className="bi bi-shield-lock fs-1 text-primary"></i>
          <h2 className="mt-3">Two-Factor Authentication</h2>
          <p className="text-muted">
            Enter the 6-digit code from your authenticator app
          </p>
        </div>
        
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}
        
        <div className="d-flex justify-content-center gap-2 mb-4">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              className="form-control text-center fs-4"
              style={{ 
                width: '50px', 
                height: '60px',
                fontSize: '24px',
                fontWeight: 'bold'
              }}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={isLoading}
              autoComplete="one-time-code"
            />
          ))}
        </div>
        
        <button
          type="button"
          className="btn btn-primary w-100 mb-3"
          onClick={() => handleVerify()}
          disabled={isLoading || code.join('').length !== 6}
        >
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Verifying...
            </>
          ) : (
            'Verify'
          )}
        </button>
        
        <div className="text-center">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to Login
          </button>
        </div>
        
        <div className="mt-4 text-center">
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            Open your authenticator app (Google Authenticator, Microsoft Authenticator, etc.) 
            to get your verification code.
          </small>
        </div>
      </div>
    </div>
  );
};

export default MFAVerification;
